/**
 * Orders API Service
 *
 * Provides API functions for order management.
 */

import { apiClient } from '@/services/api';
import type {
  AddCDInput,
  CreateOrderInput,
  Order,
  OrdersListParams,
  OrdersListResponse,
  OrderTransaction,
  UpdateOrderInput,
} from '../types';

/**
 * Orders API functions
 */
export const ordersApi = {
  /**
   * Create a new order
   */
  create: async (data: CreateOrderInput): Promise<Order> => {
    return apiClient.post<Order>('/orders', data);
  },

  /**
   * List orders with optional filters
   */
  list: async (params?: OrdersListParams): Promise<OrdersListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/orders?${queryString}` : '/orders';

    return apiClient.get<OrdersListResponse>(endpoint);
  },

  /**
   * Get a single order by ID
   */
  getById: async (id: string): Promise<Order> => {
    return apiClient.get<Order>(`/orders/${id}`);
  },

  /**
   * Update an order
   */
  update: async (id: string, data: UpdateOrderInput): Promise<Order> => {
    return apiClient.put<Order>(`/orders/${id}`, data);
  },

  /**
   * Delete (soft-delete) an order
   */
  delete: async (id: string): Promise<void> => {
    return apiClient.delete<void>(`/orders/${id}`);
  },

  /**
   * Add a CD (transaction) to an order
   */
  addCD: async (orderId: string, data: AddCDInput): Promise<OrderTransaction> => {
    return apiClient.post<OrderTransaction>(`/orders/${orderId}/cds`, data);
  },

  /**
   * List CDs (transactions) in an order
   */
  listCDs: async (orderId: string): Promise<OrderTransaction[]> => {
    return apiClient.get<OrderTransaction[]>(`/orders/${orderId}/cds`);
  },
};

export default ordersApi;
